<template>
    <div class="min-h-screen bg-gray-50 p-2 sm:p-6">
        <div class="container mx-auto">
            <h1 class="text-base sm:text-lg font-bold mb-3 sm:mb-4">
                <!--프로필-->
                내 정보 관리
            </h1>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-6">
                <!-- 프로필 정보 (왼쪽) -->
                <div class="bg-white rounded-lg shadow p-4 sm:p-6">
                    <div class="flex flex-col items-center mb-4 sm:mb-6">
                        <div
                            class="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-blue-100 flex items-center justify-center mb-3 sm:mb-4">
                            <span class="text-base sm:text-lg font-bold text-blue-700">{{ authStore.user?.branchCode ||
                                'BR001' }}</span>
                        </div>
                        <h2 class="text-base sm:text-lg font-semibold">{{ authStore.user?.branchName || '김지연' }}</h2>
                    </div>

                    <div class="space-y-3 sm:space-y-4">
                        <div class="p-2.5 sm:p-3 bg-gray-50 rounded-lg">
                            <h3 class="text-xs font-medium text-gray-500">대표자</h3>
                            <p class="mt-1 text-sm font-medium">{{ authStore.user?.name }}</p>
                        </div>
                        <div class="p-2.5 sm:p-3 bg-gray-50 rounded-lg">
                            <h3 class="text-xs font-medium text-gray-500">연락처</h3>
                            <p class="mt-1 text-sm font-medium">{{ authStore.user?.hp }}</p>
                        </div>
                    </div>

                    <div class="mt-4 sm:mt-6">
                        <button @click="handlePasswordChange"
                            class="w-full py-2 sm:py-2.5 px-3 sm:px-4 border border-gray-300 hover:border-gray-400 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
                            <LockIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            비밀번호 변경
                        </button>
                    </div>
                </div>

                <!-- 이력서 목록 (오른쪽) -->
                <div class="md:col-span-2 bg-white rounded-lg shadow">
                    <div class="p-4 sm:p-6 border-b">
                        <div class="flex justify-between items-center">
                            <h2 class="text-base sm:text-lg font-semibold">이력서 목록</h2>
                            <button @click="router.push('/resume/new')"
                                class="px-2.5 sm:px-4 py-1.5 sm:py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md flex items-center transition-colors text-xs sm:text-sm">
                                <PlusIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" />
                                새 이력서
                            </button>
                        </div>
                    </div>

                    <div class="divide-y">
                        <div v-for="resume in resumes" :key="resume.id"
                            class="p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer"
                            @click="viewResume(resume)">
                            <div class="flex items-center space-x-2 sm:space-x-4">
                                <div class="flex-shrink-0">
                                    <img :src="getProfileImageUrl(resume.profile_image)" alt="프로필 사진"
                                        class="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover border border-gray-200" />
                                </div>
                                <div class="flex-1 min-w-0">
                                    <div class="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                        <div class="flex items-center gap-2 min-w-0">
                                            <h3 class="text-xs sm:text-sm font-medium text-gray-900 truncate">{{
                                                resume.name }}</h3>
                                            <span class="flex-shrink-0 text-gray-500">
                                                <UserIcon v-if="resume.sex === 0"
                                                    class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-400" />
                                                <UserIcon v-else class="w-3.5 h-3.5 sm:w-4 sm:h-4 text-pink-400" />
                                            </span>
                                            <span
                                                :class="`text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap ${resume.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`">
                                                {{ resume.is_active ? '활성화' : '비활성화' }}
                                            </span>
                                        </div>
                                        <div class="text-xs text-gray-500 flex items-center whitespace-nowrap">
                                            <CalendarIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                                            {{ formatDate(resume.dob) }}
                                        </div>
                                        <div class="text-xs text-gray-500 flex items-center whitespace-nowrap">
                                            <PhoneIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                                            {{ resume.hp }}
                                        </div>
                                    </div>
                                </div>

                                <div class="flex space-x-1 sm:space-x-2">
                                    <button @click="viewResume(resume)"
                                        class="p-1.5 sm:p-2 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors">
                                        <EyeIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                    <button @click="editResume(resume)"
                                        class="p-1.5 sm:p-2 text-gray-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors">
                                        <FileEditIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                    <button @click.stop="deleteResume(resume)"
                                        class="p-1.5 sm:p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors">
                                        <Trash2Icon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div v-if="resumes.length === 0"
                            class="p-8 sm:p-12 text-center text-xs sm:text-sm text-gray-500">
                            등록된 이력서가 없습니다. 새 이력서를 작성해보세요.
                        </div>
                    </div>

                    <!-- 페이지네이션 -->
                    <div v-if="totalPages > 1"
                        class="flex justify-center items-center space-x-1 sm:space-x-2 p-4 border-t">
                        <button @click="currentPage = 1" :disabled="currentPage === 1"
                            class="p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                            <ChevronsLeftIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <button @click="currentPage--" :disabled="currentPage === 1"
                            class="p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                            <ChevronLeftIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>

                        <div class="flex items-center space-x-1">
                            <div v-for="page in displayedPages" :key="page" class="inline-flex">
                                <button v-if="page !== '...'" @click="currentPage = page" :class="[
                                    'px-2 sm:px-3 py-1 rounded-md text-xs',
                                    currentPage === page
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-500 hover:bg-gray-100'
                                ]">
                                    {{ page }}
                                </button>
                                <span v-else class="px-1 sm:px-2 text-gray-400 text-xs">...</span>
                            </div>
                        </div>

                        <button @click="currentPage++" :disabled="currentPage === totalPages"
                            class="p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                            <ChevronRightIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                        <button @click="currentPage = totalPages" :disabled="currentPage === totalPages"
                            class="p-1.5 sm:p-2 text-gray-500 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">
                            <ChevronsRightIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- 이력서 상세보기 모달 -->
            <ResumePage v-if="selectedResume" :is-open="isResumeModalOpen" :resume-data="selectedResume"
                :is-edit-mode="isEditMode" @close="closeResumeModal" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import resumeAPI from '@/api/resume/resume'
import { 
    CalendarIcon,
    EyeIcon,
    FileEditIcon,
    PlusIcon,
    LockIcon,
    UserIcon,
    PhoneIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ChevronsLeftIcon,
    ChevronsRightIcon,
    Trash2Icon
} from 'lucide-vue-next'
import ResumePage from './ResumePage.vue'
import Swal from 'sweetalert2'

const router = useRouter()
const authStore = useAuthStore()

// 페이지네이션 관련 상태
const currentPage = ref(1)
const itemsPerPage = 4
const resumes = ref([])
const totalPages = ref(0)
const isResumeModalOpen = ref(false)
const selectedResume = ref(null)
const isEditMode = ref(false)

// 이력서 목록 조회
const fetchResumes = async () => {
  try {
    const response = await resumeAPI.getResumes(currentPage.value, itemsPerPage)
    resumes.value = response.data.resumes
    totalPages.value = response.data.totalPages
  } catch (error) {
    console.error('이력서 목록 조회 중 오류:', error)
  }
}

// 컴포넌트 마운트 시 이력서 목록 조회
onMounted(() => {
  fetchResumes()
})

// 페이지 변경 시 이력서 목록 다시 조회
watch(currentPage, () => {
  fetchResumes()
})

const handlePasswordChange = async () => {
    try {
        // 현재 로그인된 사용자의 매장 코드와 인증 토큰을 사용
        router.push({
            name: 'ResetPassword',
            query: { 
                branchCode: authStore.user?.branchCode,
                verified: 'true',
                token: authStore.token // 현재 인증 토큰 사용
            }
        })
    } catch (error) {
        console.error('비밀번호 변경 페이지 이동 중 오류:', error)
    }
}

// 표시할 페이지 번호 계산
const displayedPages = computed(() => {
    const pages = []
    const maxDisplayed = 5 // 최대 표시할 페이지 번호 개수

    if (totalPages.value <= maxDisplayed) {
        // 전체 페이지가 maxDisplayed 이하면 모든 페이지 표시
        for (let i = 1; i <= totalPages.value; i++) {
            pages.push(i)
        }
    } else {
        // 현재 페이지 주변의 페이지 번호만 표시
        const leftBound = Math.max(1, currentPage.value - Math.floor(maxDisplayed / 2))
        const rightBound = Math.min(totalPages.value, leftBound + maxDisplayed - 1)

        if (leftBound > 1) pages.push(1, '...')
        for (let i = leftBound; i <= rightBound; i++) {
            pages.push(i)
        }
        if (rightBound < totalPages.value) pages.push('...', totalPages.value)
    }

    return pages
})

const viewResume = async (resume) => {
    try {
        const response = await resumeAPI.getResumeDetail(resume.id)
        selectedResume.value = response.data
        isResumeModalOpen.value = true
    } catch (error) {
        console.error('이력서 상세 조회 중 오류:', error)
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: '이력서 상세 정보를 불러오는데 실패했습니다.'
        })
    }
}

const editResume = async (resume) => {
    try {
        const response = await resumeAPI.getResumeDetail(resume.id)
        selectedResume.value = response.data
        isResumeModalOpen.value = true
        isEditMode.value = true
    } catch (error) {
        console.error('이력서 상세 조회 중 오류:', error)
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: '이력서 상세 정보를 불러오는데 실패했습니다.'
        })
    }
}

const closeResumeModal = () => {
    isResumeModalOpen.value = false
    selectedResume.value = null
    isEditMode.value = false
}

const deleteResume = async (resume) => {
    try {
        const result = await Swal.fire({
            title: '삭제하시겠습니까?',
            text: "삭제된 이력서는 복구할 수 없습니다.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: '삭제',
            cancelButtonText: '취소'
        })

        if (result.isConfirmed) {
            await resumeAPI.deleteResume(resume.id)
            await fetchResumes() // 목록 새로고침
            
            Swal.fire(
                '삭제 완료',
                '이력서가 삭제되었습니다.',
                'success'
            )
        }
    } catch (error) {
        console.error('이력서 삭제 중 오류:', error)
        Swal.fire(
            '오류 발생',
            '이력서 삭제 중 오류가 발생했습니다.',
            'error'
        )
    }
}

const getProfileImageUrl = (imageName) => {
    if (!imageName) return ''
    
    const resume = resumes.value.find(r => r.profile_image === imageName)
    if (resume?.is_new) {
        return `${process.env.VUE_APP_S3_URL}/_static/resume/profile/thumb/${resume.upload_year}/${imageName}`
    }
    return `https://work-api.agabang.com:6300/images/resume/${imageName}`
}

const formatDate = (date) => {
    if (!date) return ''
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}.${month}.${day}`
}
</script>